import axios from 'axios';
import moment from 'moment'
import React from 'react';
import { useLocation } from "react-router-dom";
import { Accordion, AccordionItem, AccordionItemHeading, AccordionItemButton, AccordionItemPanel } from 'react-accessible-accordion';
import { MdAddShoppingCart, MdLocationOn, MdDateRange, MdAccessTime, MdDirectionsBus, MdCheckCircle, MdCheck, MdEmail, MdPhone, MdMessage, MdInfoOutline, MdCreditCard, MdSchedule, MdContentCopy } from 'react-icons/md'
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import { checkIfLoggedIn, formatPrice, getItemEffectivePrice, formatDateRange } from '../services/utilService';
import { Button, FlexboxGrid, CheckboxGroup, Checkbox, Input, IconButton, Panel, Loader } from 'rsuite';
import CloseIcon from '@rsuite/icons/Close';
import { useCart } from '../contexts/CartContext';

import CampLayout from './common/CampLayout';
import KidInfo from './KidInfo';
import StripePayment from './payment/StripePayment';
import PromoCodeInput from './common/PromoCodeInput';
import paymentService from '../services/paymentService';
import { scrollToTop } from '../utils/scrollUtils';

// Utility function to get responsive toast position
const getToastPosition = () => {
    return window.innerWidth >= 1024 ? 'top-center' : 'bottom-center';
};

// Display helpers for the camp list (mirror the season pricing rule used everywhere)
const isDiscountActive = (season) =>
    !!(season && season.discount && season.discount.percentage > 0 &&
       season.discount.endDate && new Date(season.discount.endDate) > new Date());

const seasonEffectivePrice = (season) => {
    if (!season || season.price == null) return null;
    const raw = isDiscountActive(season)
        ? season.price * (1 - season.discount.percentage / 100)
        : season.price;
    return Math.round(raw * 100) / 100;
};

// Pull an age hint like "7+" out of a theme name such as "Curiosity Lab 7+"
const splitAgeChip = (theme) => {
    const match = theme && theme.match(/^(.*?)\s*(\d+\s*\+)\s*$/);
    if (!match) return { title: theme, age: null };
    return { title: match[1], age: match[2].replace(/\s+/g, '') };
};

const apiUrl = process.env.REACT_APP_SERVER_API_URL;

const Book = () => {

    const [kids, setKids] = React.useState([]);
    const [seasons, setSeasons] = React.useState([]);
    const [camps, setCamps] = React.useState([]);
    // const [weeks, setWeeks] = React.useState({});
    const [allWeeks, setAllWeeks] = React.useState([]);
    const [locations, setLocations] = React.useState([]);
    // Starts true so the first paint shows the loader instead of flashing
    // empty-state UI (e.g. the "Add a child" banner) before data arrives
    const [isLoading, setIsLoading] = React.useState(true);
    const [kidsLoaded, setKidsLoaded] = React.useState(false);
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const location = useLocation();

    // Determine step based on current path
    const isReviewPage = location.pathname === '/review';
    const [step, setStep] = React.useState(isReviewPage ? 2 : (location.state?.step || 1));
    const [selectedKids, setSelectedKids] = React.useState([]);
    const [createKid, setCreateKid] = React.useState(false); // Add-a-child modal (reused from Manage Kids)
    const [orderNumberCopied, setOrderNumberCopied] = React.useState(false); // transient copy feedback on the confirmation page
    // const [kidId, setKidId] = React.useState(null);
    const [selectedLocations, setSelectedLocations] = React.useState([]);

    const [order, setOrder] = React.useState(null);
    
    // Use cart context instead of localStorage
    const { cartItems: items, addMultipleToCart, removeByWeekAndKid, updateCartItem, clearCart: clearCartContext, fetchCart } = useCart();

    const [comments, setComments] = React.useState("");
    
    // Payment state
    const [paymentClientSecret, setPaymentClientSecret] = React.useState(null);
    const [paymentAmount, setPaymentAmount] = React.useState(0);
    const [paymentLoading, setPaymentLoading] = React.useState(false);
    const [paymentMethod, setPaymentMethod] = React.useState('pay_now'); // 'pay_now' or 'pay_later'
    const [addingToCartWeekId, setAddingToCartWeekId] = React.useState(null); // Track which week is being added to cart
    const [removingFromCart, setRemovingFromCart] = React.useState(null); // Track which item is being removed from cart
    
    // Promo code state
    const [appliedPromoCode, setAppliedPromoCode] = React.useState(null);
    const [originalAmount, setOriginalAmount] = React.useState(0);
    const [fullSubtotal, setFullSubtotal] = React.useState(0);
    const [seasonDiscountTotal, setSeasonDiscountTotal] = React.useState(0);
    const [discountAmount, setDiscountAmount] = React.useState(0);
    const [finalAmount, setFinalAmount] = React.useState(0);

    const navigate = useNavigate();

    // Calculate cart totals
    const calculateTotals = React.useCallback(() => {
        const rawTotal = items.reduce((total, item) => total + getItemEffectivePrice(item), 0);

        // Full subtotal before any discount (sum of season.price for each item)
        const subtotalBeforeDiscounts = items.reduce((total, item) => {
            const seasonPrice = Number(item?.week?.season?.price);
            if (!isNaN(seasonPrice) && seasonPrice > 0) {
                return total + seasonPrice;
            }
            // fallback to item.price if season info missing
            const fallback = typeof item.price === 'string'
                ? Number(item.price.replace(/[$,]/g, ''))
                : Number(item.price) || 0;
            return total + fallback;
        }, 0);

        setOriginalAmount(rawTotal);
        setFullSubtotal(subtotalBeforeDiscounts);
        setSeasonDiscountTotal(Math.max(0, subtotalBeforeDiscounts - rawTotal));

        if (appliedPromoCode) {
            setFinalAmount(rawTotal - discountAmount);
        } else {
            setFinalAmount(rawTotal);
            setDiscountAmount(0);
        }
    }, [items, appliedPromoCode, discountAmount]);

    // Handle promo code application
    const handlePromoCodeApplied = (promoInfo) => {
        setAppliedPromoCode({
            code: promoInfo.code,
            promoCodeId: promoInfo.promoCodeId,
            discount: promoInfo.discount,
            description: promoInfo.description,
            discountType: promoInfo.discountType
        });
        setDiscountAmount(promoInfo.discount);
        if (promoInfo.discount > 0) {
            toast.success(`Promo code "${promoInfo.code}" applied! You saved $${formatPrice(promoInfo.discount)} extra`, {
                position: getToastPosition()
            });
        } else {
            toast.info(`Promo code "${promoInfo.code}" applied, but your existing discount is already as good or better.`, {
                position: getToastPosition()
            });
        }
    };

    // Handle promo code removal
    const handlePromoCodeRemoved = () => {
        setAppliedPromoCode(null);
        setDiscountAmount(0);
        toast.info('Promo code removed', {
            position: getToastPosition()
        });
    };

    React.useEffect(() => {
        calculateTotals();
    }, [calculateTotals]);

    React.useEffect(() => {
        // Update step based on current path
        if (location.pathname === '/thank-you') {
            // Check if order data exists from navigation state
            if (location.state?.order) {
                setStep(4);
                setOrder(location.state.order);
                if (location.state?.paymentAmount) {
                    setPaymentAmount(location.state.paymentAmount);
                }
            } else {
                // No order data available (page refresh) - redirect to history page
                navigate('/history');
            }
        } else if (location.pathname === '/review') {
            setStep(2);
            // Ensure scroll to top when Review page loads (iOS fix)
            scrollToTop();
        } else if (location.pathname === '/payment' || location.pathname === '/payment-complete') {
            setStep(3);
        } else if (location.pathname === '/book') {
            setStep(1);
        } else if (location.state?.step) {
            setStep(location.state?.step);
        }
    },[location, navigate]);

    // Handle Stripe redirect-based payment methods (3DS, bank redirects) returning
    // to /payment-complete. Without this, the redirect would land on a 404.
    const paymentRecoveryRef = React.useRef(false);
    React.useEffect(() => {
        if (location.pathname !== '/payment-complete') return;
        if (paymentRecoveryRef.current) return;
        paymentRecoveryRef.current = true;

        const params = new URLSearchParams(location.search);
        const redirectStatus = params.get('redirect_status');
        const paymentIntentId = params.get('payment_intent');

        if (redirectStatus !== 'succeeded' || !paymentIntentId) {
            toast.error('Payment was not completed. Please try again.', { position: getToastPosition() });
            navigate('/review');
            return;
        }

        let pending = null;
        try {
            pending = JSON.parse(sessionStorage.getItem('pendingCheckout') || 'null');
        } catch (e) {
            pending = null;
        }

        if (!pending || !Array.isArray(pending.items) || pending.items.length === 0) {
            // Payment succeeded at Stripe but we lost the cart snapshot.
            // The webhook only updates existing orders, so we can't auto-create here — send the user to their orders list and ask them to contact support.
            toast.error('Payment received, but we couldn\'t finalize your booking automatically. Please check Orders or contact support.', { position: getToastPosition() });
            navigate('/history');
            return;
        }

        paymentService.completeOrderAfterPayment(
            paymentIntentId,
            pending.items,
            pending.comments || '',
            pending.promoCodeId || null
        ).then((orderResponse) => {
            sessionStorage.removeItem('pendingCheckout');
            resetCart();
            setAppliedPromoCode(null);
            setOriginalAmount(0);
            setDiscountAmount(0);
            setFinalAmount(0);
            navigate('/thank-you', { state: { order: orderResponse.order, paymentAmount: pending.amount || 0 } });
            scrollToTop();
            toast.success('Payment successful! Your booking is confirmed.', { position: getToastPosition() });
        }).catch((error) => {
            console.error('Error completing order after redirect:', error);
            toast.error('Payment may have succeeded but we couldn\'t finalize your booking. Please check Orders or contact support.', { position: getToastPosition() });
            navigate('/history');
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);
    
    React.useEffect(() => {
        checkIfLoggedIn().then(() => {
            if (!localStorage.getItem("user-id")) {
                navigate("/login");
            } else {
                let mounted = true;
                setIsLoading(true);
                // Fetch cart when component loads
                fetchCart();
                axios.get(`${apiUrl}/users/${localStorage.getItem('user-id')}/kids`)
                    .then((response) => {
                        if (mounted) {
                            // setKids(response.data.map(k => ({ value: k._id, label: k.firstName + " " + k.lastName })));
                            setKids(response.data);
                            if (response.data.length > 0) {
                                setSelectedKids([response.data[0]._id]);
                                // setKidId(response.data[0]._id);
                            }
                        }
                    })
                    .finally(() => {
                        if (mounted) {
                            setKidsLoaded(true);
                        }
                    });
                axios.get(`${apiUrl}/weeks`)
                    .then((response) => {
                        if (mounted) {
                            setAllWeeks(response.data);

                            const uniqueSeasonsIds = [];
                            const uniqueSeasons = [];
                            const uniqueCampIds = [];
                            const uniqueCamps = [];
                            const uniqueLocationIds = [];
                            const uniqueLocations = [];                    
                            response.data.map(week => {
                                if (uniqueSeasonsIds.indexOf(week.season._id) === -1) {
                                    uniqueSeasonsIds.push(week.season._id);
                                    uniqueSeasons.push(week.season);
                                }
                                let campKey = (week.season._id + "-" + week.camp._id + "-" + week.theme).replace(/\s+/g, '_');
                                if (uniqueCampIds.indexOf(campKey) === -1) {
                                    uniqueCampIds.push(campKey);
                                    week.camp.seasonId = week.season._id;
                                    week.camp.theme = week.theme;
                                    week.camp._key = campKey;
                                    uniqueCamps.push(week.camp);
                                }
                                let locationKey = (week.season._id + "-" + week.camp._id + "-" + week.theme + "-" + week.location._id).replace(/\s+/g, '_');
                                if (uniqueLocationIds.indexOf(locationKey) === -1) {
                                    uniqueLocationIds.push(locationKey);
                                    week.location.seasonId = week.season._id;
                                    week.location.campId = week.camp._id;
                                    week.location.theme = week.theme;
                                    week.location._key = locationKey;
                                    uniqueLocations.push(week.location);
                                }
                                return null;
                            });
                            setCamps(uniqueCamps.sort((a, b) => a.priority > b.priority ? 1 : -1));
                            setSeasons(uniqueSeasons);
                            setLocations(uniqueLocations);
                        }
                    })
                    .finally(() => {
                        if (mounted) {
                            setIsLoading(false);
                        }
                    });
                return () => mounted = false;
            }
        }
        );

    }, [navigate, fetchCart]);

    function updateSelectedLocations(location) {
        let newSelectedLocations = selectedLocations.filter(loc => loc.seasonId !== location.seasonId);
        newSelectedLocations.push(location);
        setSelectedLocations(newSelectedLocations);
    }

    function resetSelectedLocations(seasonId) {
        let newSelectedLocations = selectedLocations.filter(loc => loc.seasonId !== seasonId);
        setSelectedLocations(newSelectedLocations);
    }

    // Create a child from the booking page (mirrors ManageKids.createOrUpdateKid,
    // create branch only — KidInfo is only opened here in create mode) and select
    // it for booking so "Add kids first" buttons become active immediately.
    function createOrUpdateKid(kidId, kidData) {
        axios.post(`${apiUrl}/kids`, kidData)
            .then((response) => {
                if (kids.length === 0) toast.success('Great! You can now book camps for your child.', { position: getToastPosition() });
                setKids([...kids, response.data]);
                setSelectedKids([...selectedKids, response.data._id]);
            })
            .catch((error) => {
                console.log(error);
            });
    }

    function setKidsForBooking(e) {
        setSelectedKids(e);
        // if (e.length !== 0) {
        //     setSelectedKids(e);
        // } else {
        //     toast.error('At least one kid should be selected', {});
        // }
    }

    function changeDayOption(event, week) {
        week.dayOption = event.target.value;
    }

    // Calculate available bus options for a cart item
    function getBusOptions(item) {
        let busOptions = ["None", "Door-to-door"];
        if (item.week.location && item.week.location.busOptions) {
            busOptions.push(...item.week.location.busOptions);
        }
        return busOptions;
    }

    async function addToCart(week) {
        // Prevent double clicks
        if (addingToCartWeekId === week._id) {
            return;
        }

        let duplicatedWeeks = selectedKids.map(k =>
            items.filter((i) => moment(i.week.startDate).isSame(moment(week.startDate), 'day') && i.kid._id === k)
        ).reduce((a, b) => a + b, '');

        if (duplicatedWeeks.length > 0) {
            toast.error('You have already booked a camp for this week. Please check your cart.', { position: getToastPosition() });
            return;
        } 

        if (!week.dayOption) week.dayOption = 'Full day'; // todo avoid hard-coding
        
        // Calculate effective price considering discount
        let price = week.season.price;
        if (week.season.discount && 
            week.season.discount.percentage > 0 && 
            week.season.discount.endDate && 
            new Date(week.season.discount.endDate) > new Date()) {
            price = price * (1 - week.season.discount.percentage / 100);
        }

        let busOptions = ["None", "Door-to-door"];
        if (week.location.busOptions) busOptions.push(...week.location.busOptions);
        
        // Set loading state
        setAddingToCartWeekId(week._id);
        
        // Add items to cart using batch operation to prevent multiple toasts
        try {
            const itemsToAdd = selectedKids.map(kidId => ({
                week: week._id,
                dayOption: week.dayOption,
                price: price.toString(),
                kid: kidId,
                busOption: "None"
            }));
            await addMultipleToCart(itemsToAdd);
            // Success toast is handled by CartContext
        } catch (error) {
            console.error('Error adding to cart:', error);
            // Error toast is handled by CartContext
        } finally {
            // Clear loading state
            setAddingToCartWeekId(null);
        }
    }

    async function deleteFromCart(weekId, kidId) {
        // Prevent double clicks
        const removeKey = kidId ? `${weekId}-${kidId}` : weekId;
        if (removingFromCart === removeKey) {
            return;
        }

        // Set loading state
        setRemovingFromCart(removeKey);
        
        try {
            await removeByWeekAndKid(weekId, kidId);
        } catch (error) {
            console.error('Error removing from cart:', error);
            // Error toast is handled by CartContext
        } finally {
            // Clear loading state
            setRemovingFromCart(null);
        }
    }

    async function handleBusOptionChange(newBusOption, updatedItem) {
        try {
            // Find the cart item to update
            const cartItem = items.find(item => 
                item.week._id === updatedItem.week._id && item.kid._id === updatedItem.kid._id
            );
            if (cartItem && cartItem._id) {
                await updateCartItem(cartItem._id, { busOption: newBusOption });
            }
        } catch (error) {
            console.error('Error updating bus option:', error);
            // Error toast is handled by CartContext
        }
    }

    async function resetCart() {
        try {
            await clearCartContext();
        } catch (error) {
            console.error('Error clearing cart:', error);
        }
    }

    async function proceedToPayment() {
        setIsSubmitting(true);
        try {
            if (paymentMethod === 'pay_later') {
                // Create Pay Later order
                const orderResponse = await paymentService.createPayLaterOrder(
                    items, 
                    comments, 
                    appliedPromoCode?.promoCodeId
                );
                setOrder(orderResponse.order);
                resetCart();
                // Clear promo code state
                setAppliedPromoCode(null);
                setOriginalAmount(0);
                setDiscountAmount(0);
                setFinalAmount(0);
                setStep(4); // Go to success step
                navigate('/thank-you', { state: { order: orderResponse.order } });
                scrollToTop(); // Scroll to top when going to Thank You page
                toast.success('Order confirmed! Our team will contact you to arrange payment.', { position: getToastPosition() });
            } else {
                // Create payment intent with cart data (no order created yet)
                setPaymentLoading(true);
                const paymentResponse = await paymentService.createCartPaymentIntent(
                    items,
                    comments,
                    appliedPromoCode?.promoCodeId
                );

                // Stash checkout state so a Stripe redirect (3DS / bank flows)
                // returning to /payment-complete can still finalize the order.
                try {
                    sessionStorage.setItem('pendingCheckout', JSON.stringify({
                        items,
                        comments,
                        promoCodeId: appliedPromoCode?.promoCodeId || null,
                        amount: paymentResponse.amount
                    }));
                } catch (e) {
                    console.warn('Could not stash pending checkout:', e);
                }

                setPaymentClientSecret(paymentResponse.clientSecret);
                setPaymentAmount(paymentResponse.amount);
                setStep(3); // Go to payment step
                navigate('/payment'); // Keep URL and step in sync so a location change can't bounce us back to Review
            }
        } catch (error) {
            console.error('Error processing order:', error);
            toast.error('Oops! Something went wrong. We\'re looking into it.', { position: getToastPosition() });
        } finally {
            setIsSubmitting(false);
            setPaymentLoading(false);
        }
    }

    async function handlePaymentSuccess(paymentResult) {
        try {
            // Complete the order creation after successful payment
            const orderResponse = await paymentService.completeOrderAfterPayment(
                paymentResult.paymentIntent.id, 
                items, 
                comments,
                appliedPromoCode?.promoCodeId
            );
            
            setOrder(orderResponse.order);
            resetCart();
            // Clear promo code state
            setAppliedPromoCode(null);
            setOriginalAmount(0);
            setDiscountAmount(0);
            setFinalAmount(0);
            setStep(4); // Go to confirmation step
            navigate('/thank-you', { state: { order: orderResponse.order, paymentAmount: paymentResult.paymentIntent.amount } });
            scrollToTop(); // Scroll to top when going to Thank You page
            toast.success('Payment successful! Your booking is confirmed.', { position: getToastPosition() });
        } catch (error) {
            console.error('Error completing order:', error);
            toast.error('Payment successful but there was an issue completing your order. Please contact support.', { position: getToastPosition() });
        }
    }

    function handlePaymentError(error) {
        toast.error(`Payment failed: ${error}`, { position: getToastPosition() });
    }

    // ---- Presentational helpers for the expanded camp (locations + weeks) ----

    // Price/day-option line rendered once per selected location, instead of per week row
    const renderWeekListHeader = (camp, season) => {
        const price = seasonEffectivePrice(season);
        if (price == null) return null;
        return (
            <div className="text-sm text-gray-700 mb-3 pb-2 border-b border-gray-200">
                {camp.options.length === 1 && <>{camp.options[0].name} · </>}
                <span className="font-semibold">${formatPrice(price)}</span> per child
                {isDiscountActive(season) && (
                    <span className="text-gray-500">
                        {' '}(was <span className="line-through">${formatPrice(season.price)}</span>)
                    </span>
                )}
            </div>
        );
    };

    // Capacity chip — renders only if the weeks payload ever carries spotsLeft; no data invented
    const renderAvailability = (week) => {
        if (week.spotsLeft == null) return null;
        if (week.spotsLeft <= 0) {
            return <span className="ml-2 inline-block text-xs font-medium bg-gray-100 text-gray-500 rounded-full px-2 py-0.5">Full</span>;
        }
        return <span className="ml-2 inline-block text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full px-2 py-0.5">{week.spotsLeft} spots left</span>;
    };

    // Add / Adding… / ✓ Added (+ Remove) states for a week row
    const renderWeekAction = (week) => {
        const inCart = items.filter(item => item.week._id === week._id).length > 0;
        const isFull = week.spotsLeft != null && week.spotsLeft <= 0;

        if (!inCart) {
            if (isFull) {
                return (
                    <div className="rounded-lg p-2 flex items-center justify-center text-sm bg-gray-100 text-gray-400 cursor-not-allowed">
                        Full
                    </div>
                );
            }
            if (selectedKids.length === 0) {
                if (kids.length === 0) {
                    // No children on the account: lead straight into the
                    // add-a-child flow instead of dead-ending.
                    return (
                        <div
                            role="button"
                            tabIndex={0}
                            className="rounded-lg p-2 flex items-center justify-center text-sm border bg-gray-50 border-gray-300 cursor-pointer hover:bg-gray-100"
                            onClick={() => setCreateKid(true)}
                            onKeyDown={(e) => { if (e.key === 'Enter') setCreateKid(true); }}
                        >
                            <MdAddShoppingCart size="1.5em" className="mr-1 text-gray-500" />
                            <span className="font-medium text-gray-600">Add kids first</span>
                        </div>
                    );
                }
                // Children exist but none is selected — adding is already
                // impossible in this state, so show a disabled Add (the
                // selection area explains what to do).
                return (
                    <div className="rounded-lg p-2 flex items-center justify-center text-sm bg-gray-100 cursor-not-allowed" aria-disabled="true">
                        <MdAddShoppingCart size="1.5em" className="mr-1 text-gray-400" />
                        <span className="font-medium text-gray-400">Add</span>
                    </div>
                );
            }
            const adding = addingToCartWeekId === week._id;
            const addLabel = selectedKids.length > 1 ? `Add for ${selectedKids.length} children` : 'Add';
            return (
                <div
                    role="button"
                    tabIndex={0}
                    className={`rounded-lg p-2 flex items-center justify-center text-sm border ${
                        adding
                            ? 'bg-gray-100 border-gray-300 cursor-not-allowed'
                            : 'cursor-pointer bg-blue-50 border-blue-200 hover:bg-blue-100'
                    }`}
                    onClick={() => !adding && addToCart(week)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !adding) addToCart(week); }}
                >
                    {adding ? (
                        <>
                            <Loader size="sm" />
                            <span className="ml-1 font-medium text-gray-500">Adding…</span>
                        </>
                    ) : (
                        <>
                            <MdAddShoppingCart size="1.5em" className="mr-1 text-blue-600" />
                            <span className="font-medium text-blue-700 whitespace-nowrap">{addLabel}</span>
                        </>
                    )}
                </div>
            );
        }

        const removing = removingFromCart === week._id;
        return (
            <div className="flex items-center justify-center gap-2">
                <span className="inline-flex items-center gap-1 rounded-full border border-green-300 bg-green-50 text-green-700 text-sm font-medium px-3 py-1 whitespace-nowrap">
                    <MdCheckCircle size="1.1em" /> Added
                </span>
                {removing ? (
                    <Loader size="sm" />
                ) : (
                    <button
                        type="button"
                        className="text-sm text-gray-500 hover:text-red-600 underline bg-transparent border-0 cursor-pointer p-0"
                        onClick={() => deleteFromCart(week._id)}
                    >
                        Remove
                    </button>
                )}
            </div>
        );
    };

    return (
        <CampLayout page="book" step={step} onRemoveItem={(item) => deleteFromCart(item.week._id, item.kid._id)}>

            <div className="px-7 pb-7 w-full">
                {
                    <>
                        {step === 1 && <h1 className="page-title mb-4">Book a Camp</h1>}
                        {step === 1 && kidsLoaded && kids.length === 0 && (
                            <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4">
                                <MdInfoOutline className="text-blue-600 flex-shrink-0" size={20} />
                                <p className="text-sm text-blue-900 m-0 flex-1" style={{ minWidth: 200 }}>
                                    Add your child's details once — then you can book any camp.
                                </p>
                                <Button appearance="primary" size="sm" onClick={() => setCreateKid(true)}>
                                    Add a child
                                </Button>
                            </div>
                        )}
                        <div style={{ paddingBottom: 10 }} hidden={step !== 1 || kids.length === 0}>
                            {kids.length === 1 ? (
                                <div className="flex items-center gap-3 flex-wrap">
                                    <div className="inline-flex items-center gap-1.5 bg-blue-50 border border-blue-200 text-blue-800 rounded-full px-3 py-1 text-sm">
                                        Booking for:
                                        <span className="font-semibold">{kids[0].firstName} {kids[0].lastName}</span>
                                    </div>
                                    <button
                                        type="button"
                                        className="text-sm text-blue-600 hover:text-blue-800 underline bg-transparent border-0 cursor-pointer p-0"
                                        onClick={() => setCreateKid(true)}
                                    >
                                        + Add another child
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <p className="mb-1 text-base">Select the children you are booking camps for</p>
                                    {selectedKids.length === 0 ? (
                                        <p className="text-sm text-yellow-700 mb-1">Select at least one child to book camps.</p>
                                    ) : (
                                        <p className="text-sm text-gray-500 mb-1">Camps you add will be booked for every selected child.</p>
                                    )}
                                    <CheckboxGroup value={selectedKids} onChange={(e) => setKidsForBooking(e)}>
                                        {kids.map((kid) =>
                                            <Checkbox key={kid._id} value={kid._id}>{kid.firstName} {kid.lastName}</Checkbox>
                                        )}
                                    </CheckboxGroup>
                                </>
                            )}
                        </div>
                        
                        {/* Divider */}
                        <div className="border-t border-gray-300 my-6" hidden={step !== 1 || kids.length === 0}></div>
                        
                        {isLoading && step === 1 &&
                            <div className="flex justify-center items-center p-8">
                                <Loader size="lg" content="Loading camps..." />
                            </div>
                        }
                        {!isLoading && step === 1 &&
                            <div >
                                {seasons.map((season) =>
                                <div key={season._id} >
                                    <div className="mb-6">
                                        <h2 className="text-xl lg:text-2xl font-bold mb-1">
                                            <span className="hidden lg:inline">{season.name.toUpperCase()} ({season.dates})</span>
                                            <span className="lg:hidden">{season.name.toUpperCase()}</span>
                                        </h2>
                                        <p className="text-sm text-gray-600 lg:hidden">
                                            {season.dates}
                                        </p>
                                        {/* Price + discount shown once per season — every camp in a
                                            season shares the same season price */}
                                        {seasonEffectivePrice(season) != null && (
                                            <p className="text-sm text-gray-600 mt-1 mb-0">
                                                {isDiscountActive(season) && (
                                                    <span className="line-through mr-1">${formatPrice(season.price)}</span>
                                                )}
                                                <span className="font-semibold text-gray-800">${formatPrice(seasonEffectivePrice(season))}</span>
                                                {' '}per week per child
                                                {isDiscountActive(season) && (
                                                    <span className="ml-2 inline-block text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full px-2 py-0.5">
                                                        {season.discount.percentage}% off
                                                    </span>
                                                )}
                                            </p>
                                        )}
                                    </div>
                                    <Accordion onChange={() => resetSelectedLocations(season._id)} allowZeroExpanded={true}>
                                    {camps.filter(camp => camp.seasonId === season._id).map((camp) =>
                                        <AccordionItem key={camp._key} uuid={camp._key}>
                                            <AccordionItemHeading>
                                                <AccordionItemButton>
                                                    {(() => {
                                                        const { title, age } = splitAgeChip(camp.theme);
                                                        return (
                                                            <div className="flex items-center gap-2 flex-wrap w-full">
                                                                <span className="font-semibold text-gray-900">{title}</span>
                                                                {age && (
                                                                    <span className="text-xs font-medium bg-blue-100 text-blue-800 rounded-full px-2 py-0.5">
                                                                        ages {age}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        );
                                                    })()}
                                                </AccordionItemButton>
                                            </AccordionItemHeading>
                                            <AccordionItemPanel>
                                                <div className="w-full flex-row overflow-auto hidden lg:flex">
                                                    {/* Location tabs */}
                                                    <div className="flex flex-col w-64 border-r border-gray-200 flex-shrink-0" role="tablist">
                                                        {locations.filter(location => location.seasonId === season._id && location.campId === camp._id && location.theme === camp.theme).map((location) => {
                                                            const selected = selectedLocations.filter(loc => loc._key === location._key).length > 0;
                                                            return (
                                                                <button
                                                                    key={location._key}
                                                                    type="button"
                                                                    role="tab"
                                                                    aria-selected={selected}
                                                                    onClick={() => updateSelectedLocations(location)}
                                                                    className={"text-left px-3 py-2.5 mr-3 mb-1 rounded-r border-l-4 transition-colors " + (
                                                                        selected
                                                                            ? "border-blue-600 bg-blue-50"
                                                                            : "border-transparent bg-transparent hover:bg-gray-50"
                                                                    )}
                                                                >
                                                                    <span className={"block text-sm " + (selected ? "font-semibold text-blue-700" : "font-medium text-gray-700")}>
                                                                        {location.name}
                                                                    </span>
                                                                    {location.address && (
                                                                        <span className={"block text-xs mt-0.5 " + (selected ? "text-blue-600" : "text-gray-500")}>
                                                                            {location.address}
                                                                        </span>
                                                                    )}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                    {selectedLocations.filter(loc => loc.seasonId === season._id).length > 0 ?
                                                        <div className="ml-6 transition duration-1000 flex-grow-0 flex-shrink-0" style={{ minWidth: '340px' }}>
                                                            {renderWeekListHeader(camp, season)}
                                                            <div
                                                                className="gap-x-6 gap-y-2 items-center grid text-sm"
                                                                style={{ gridTemplateColumns: camp.options.length > 1 ? 'minmax(150px, auto) minmax(120px, auto) minmax(150px, auto)' : 'minmax(150px, auto) minmax(150px, auto)' }}
                                                            >
                                                                {allWeeks.filter(week => week.season._id === season._id && week.camp._id === camp._id && week.theme === camp.theme && week.location._id === selectedLocations.filter(loc => loc.seasonId === season._id)[0]._id).map(week =>
                                                                    <React.Fragment key={week._id}>
                                                                        <span className="whitespace-nowrap py-1">
                                                                            {formatDateRange(week.startDate, week.endDate)}
                                                                            {renderAvailability(week)}
                                                                        </span>
                                                                        {camp.options.length > 1 && (
                                                                            <select onChange={(event) => changeDayOption(event, week)} className="border border-gray-300 p-1 rounded text-sm">
                                                                                {camp.options.map((dayOption) =>
                                                                                    <option key={dayOption.name} value={dayOption.name}>{dayOption.name}</option>
                                                                                )}
                                                                            </select>
                                                                        )}
                                                                        {renderWeekAction(week)}
                                                                    </React.Fragment>
                                                                )}
                                                            </div>
                                                        </div>
                                                        :
                                                        <div className="ml-6 self-center text-sm text-gray-500">
                                                            Select a location to see available dates
                                                        </div>}
                                                </div>

                                                <div className="lg:hidden">
                                                    {locations.filter(location => location.seasonId === season._id && location.campId === camp._id && location.theme === camp.theme).map((location) =>
                                                            <Panel key={location._key} header={location.name + (location.address ? " (" + location.address + ")" : "")} collapsible bordered>
                                                                <div className="transition duration-1000 flex-grow-0 flex-shrink-0">
                                                                    {renderWeekListHeader(camp, season)}
                                                                    <div className="divide-y divide-gray-100">
                                                                        {allWeeks.filter(week => week.season._id === season._id && week.camp._id === camp._id && week.theme === camp.theme && week.location._id === location._id).map(week =>
                                                                            <div key={week._id} className="flex items-center justify-between gap-3 py-2.5">
                                                                                <div>
                                                                                    <div className="text-sm font-semibold text-gray-800">
                                                                                        {formatDateRange(week.startDate, week.endDate)}
                                                                                        {renderAvailability(week)}
                                                                                    </div>
                                                                                    {camp.options.length > 1 && (
                                                                                        <select className="mt-1 text-xs border border-gray-300 rounded px-1 py-0.5" onChange={(event) => changeDayOption(event, week)}>
                                                                                            {camp.options.map((dayOption) =>
                                                                                                <option key={dayOption.name} value={dayOption.name}>{dayOption.name}</option>
                                                                                            )}
                                                                                        </select>
                                                                                    )}
                                                                                </div>
                                                                                <div className="flex-shrink-0">
                                                                                    {renderWeekAction(week)}
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </Panel>
                                                        )
                                                    }
                                                </div>
                                                
                                            </AccordionItemPanel>
                                        </AccordionItem>
                                    )
                                    }
                                    </Accordion>
                                    <br/>
                                </div>)
                                }

                            </div>
                        }
                        {step === 2 &&
                            <>
                                {/* Summary Header */}
                                <div className="mb-6">
                                    <h1 className="page-title">Review Your Order</h1>
                                </div>

                                {/* Notice when a season discount changed since items were added and the
                                    price went up. Price decreases apply silently (the customer pays less). */}
                                {items.some(item => item.priceChanged && item.previousPrice != null && getItemEffectivePrice(item) > item.previousPrice) &&
                                    <div className="mb-6 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-800">
                                        <MdInfoOutline className="mt-0.5 flex-shrink-0" size={18} />
                                        <p className="text-sm">
                                            Prices for some items have changed since you added them — the current prices are shown below and used for your total.
                                        </p>
                                    </div>
                                }

                                {/* Booking cards (single responsive layout) */}
                                <div className="space-y-3 mb-6">
                                    {items.sort((a, b) => new Date(a.week.startDate) - new Date(b.week.startDate)).map(item =>
                                        <div key={item.week._id + item.kid._id} className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 lg:p-5">

                                            {/* Line 1: camp name + price */}
                                            <div className="flex justify-between items-start gap-3">
                                                <p className="font-semibold text-gray-900 m-0">{item.week.theme}</p>
                                                <div className="text-right flex-shrink-0">
                                                    {getItemEffectivePrice(item) < Number(item.week.season.price) && (
                                                        <div className="text-sm text-gray-500 line-through">
                                                            ${formatPrice(item.week.season.price)}
                                                        </div>
                                                    )}
                                                    <div className={`text-lg font-semibold leading-tight ${item.priceChanged ? 'text-yellow-700' : 'text-gray-900'}`}>
                                                        ${formatPrice(getItemEffectivePrice(item))}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Line 2: child */}
                                            <p className="text-sm text-gray-500 mt-0.5">for {item.kid.firstName} {item.kid.lastName}</p>

                                            {/* Line 3: location · dates · day option */}
                                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-gray-600">
                                                <span className="flex items-center">
                                                    <MdLocationOn className="text-gray-400 mr-1 flex-shrink-0" size={16} />
                                                    {item.week.location.name}{item.week.location.address ? ` · ${item.week.location.address}` : ''}
                                                </span>
                                                <span className="flex items-center">
                                                    <MdDateRange className="text-gray-400 mr-1 flex-shrink-0" size={16} />
                                                    {formatDateRange(item.week.startDate, item.week.endDate)}
                                                </span>
                                                <span className="flex items-center">
                                                    <MdAccessTime className="text-gray-400 mr-1 flex-shrink-0" size={16} />
                                                    {item.dayOption}
                                                </span>
                                            </div>

                                            {/* Line 4: transport + remove */}
                                            <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-100">
                                                <label className="flex items-center gap-2 text-sm text-gray-700 m-0">
                                                    <MdDirectionsBus className="text-gray-500 flex-shrink-0" size={18} />
                                                    <span>Transport:</span>
                                                    <select
                                                        value={item.busOption || "None"}
                                                        onChange={(e) => handleBusOptionChange(e.target.value, item)}
                                                        className="border border-gray-300 rounded-md px-2 py-1 text-sm"
                                                    >
                                                        {getBusOptions(item).map((option, index) => (
                                                            <option key={index} value={option}>{option}</option>
                                                        ))}
                                                    </select>
                                                </label>
                                                {removingFromCart === `${item.week._id}-${item.kid._id}` ? (
                                                    <div className="p-2">
                                                        <Loader size="sm" />
                                                    </div>
                                                ) : (
                                                    <IconButton
                                                        icon={<CloseIcon />}
                                                        size="sm"
                                                        appearance="subtle"
                                                        aria-label={`Remove ${item.week.theme} for ${item.kid.firstName}`}
                                                        title="Remove from order"
                                                        className="text-gray-400 hover:text-red-500 hover:bg-red-50"
                                                        onClick={() => deleteFromCart(item.week._id, item.kid._id)}
                                                        disabled={removingFromCart === `${item.week._id}-${item.kid._id}`}
                                                    />
                                                )}
                                            </div>

                                        </div>
                                    )}
                                </div>
                                
                                {/* Promo Code Input */}
                                <div className="mb-6 mt-6 lg:mt-0">
                                    <PromoCodeInput
                                        cartTotal={originalAmount}
                                        items={items}
                                        onPromoCodeApplied={handlePromoCodeApplied}
                                        onPromoCodeRemoved={handlePromoCodeRemoved}
                                        appliedPromoCode={appliedPromoCode}
                                        disabled={isSubmitting}
                                    />
                                </div>
                                
                                {/* Total Summary */}
                                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 lg:mx-0 -mx-7 lg:px-4 px-7 mt-6">
                                    <div className="mb-3">
                                        <h4 className="section-heading">Order Summary</h4>
                                        <p className="text-sm text-gray-600">
                                            {items.length} camp booking{items.length !== 1 ? 's' : ''}
                                        </p>
                                    </div>

                                    <div className="space-y-1.5 lg:max-w-sm lg:ml-auto">
                                        <div className="flex justify-between items-center text-sm">
                                            <span className="text-gray-700">Subtotal</span>
                                            <span className="text-gray-800">${formatPrice(fullSubtotal)}</span>
                                        </div>

                                        {seasonDiscountTotal > 0 && (
                                            <div className="flex justify-between items-center text-sm">
                                                <span className="text-green-700">Standard discount</span>
                                                <span className="text-green-700">-${formatPrice(seasonDiscountTotal)}</span>
                                            </div>
                                        )}

                                        {appliedPromoCode && discountAmount > 0 && (
                                            <div className="flex justify-between items-center text-sm">
                                                <span className="text-green-700">
                                                    Promo code ({appliedPromoCode.code})
                                                </span>
                                                <span className="text-green-700">
                                                    -${formatPrice(discountAmount)}
                                                </span>
                                            </div>
                                        )}

                                        {appliedPromoCode && discountAmount === 0 && (
                                            <div className="flex justify-between items-center text-sm">
                                                <span className="text-yellow-700">
                                                    Promo code ({appliedPromoCode.code})
                                                </span>
                                                <span className="text-yellow-700">
                                                    No additional saving
                                                </span>
                                            </div>
                                        )}

                                        {(seasonDiscountTotal + discountAmount) > 0 && (
                                            <div className="flex justify-between items-center text-sm font-semibold">
                                                <span className="text-green-700">You save</span>
                                                <span className="text-green-700">${formatPrice(seasonDiscountTotal + discountAmount)}</span>
                                            </div>
                                        )}

                                        <div className="border-t border-blue-200 pt-2 mt-2 flex justify-between items-end">
                                            <span className="text-gray-800 font-semibold">Total</span>
                                            <div className="text-right">
                                                <div className="text-2xl font-bold text-gray-800">
                                                    ${formatPrice(finalAmount)}<span className="currency-suffix">SGD</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Bus Transportation Notice within Order Summary */}
                                    {items.some(item => item.busOption && item.busOption !== 'None') && (
                                        <div className="mt-4 pt-4 border-t border-blue-200">
                                            <div className="flex items-start">
                                                <div className="text-blue-600 mr-3 mt-0.5">
                                                    <MdDirectionsBus size={18} />
                                                </div>
                                                <div className="flex-1">
                                                    <p className="text-sm text-red-600">
                                                        Our customer support team will contact you to arrange bus transportation details.
                                                        {items.some(item => item.busOption && item.busOption !== 'None' && !item.busOption.toLowerCase().includes('free')) && (
                                                            <span className="block mt-1 font-medium">Extra charges will apply.</span>
                                                        )}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Special Requests Section */}
                                <div className="bg-gray-50 rounded-xl p-4 mb-4 lg:mx-0 -mx-7 lg:px-4 px-7">
                                    <h4 className="section-heading mb-2">Special Requests</h4>
                                    <Input
                                        as="textarea"
                                        rows={3}
                                        placeholder="Any special requests? (optional)"
                                        value={comments}
                                        onChange={(value) => value.length <= 500 ? setComments(value) : null}
                                        className="w-full border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        style={{
                                            resize: 'vertical',
                                            minHeight: '80px'
                                        }}
                                    />
                                    <p className="text-sm text-gray-600 mt-2 mb-0">
                                        Need specific days only instead of the full week? Select the Pay Later option and describe it here — we'll arrange it with you.
                                    </p>
                                </div>

                                {/* Payment Method Selection */}
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                                    <p className="text-base font-semibold mb-3">Select Payment Method</p>
                                    <div className="space-y-3">
                                        <label className="flex items-start cursor-pointer p-3 rounded-lg border-2 transition-colors"
                                               style={{
                                                   borderColor: paymentMethod === 'pay_now' ? '#2563eb' : '#e5e7eb',
                                                   backgroundColor: paymentMethod === 'pay_now' ? '#eff6ff' : '#ffffff'
                                               }}>
                                            <input
                                                type="radio"
                                                name="paymentMethod"
                                                value="pay_now"
                                                checked={paymentMethod === 'pay_now'}
                                                onChange={(e) => setPaymentMethod(e.target.value)}
                                                className="mt-1 mr-3"
                                            />
                                            <MdCreditCard className="text-gray-600 mr-2 mt-0.5 flex-shrink-0" size={20} />
                                            <div>
                                                <p className="font-medium text-gray-800 m-0">Pay Now</p>
                                                <p className="text-sm text-gray-600 m-0">Secure payment via credit/debit card</p>
                                            </div>
                                        </label>

                                        <label className="flex items-start cursor-pointer p-3 rounded-lg border-2 transition-colors"
                                               style={{
                                                   borderColor: paymentMethod === 'pay_later' ? '#2563eb' : '#e5e7eb',
                                                   backgroundColor: paymentMethod === 'pay_later' ? '#eff6ff' : '#ffffff'
                                               }}>
                                            <input
                                                type="radio"
                                                name="paymentMethod"
                                                value="pay_later"
                                                checked={paymentMethod === 'pay_later'}
                                                onChange={(e) => setPaymentMethod(e.target.value)}
                                                className="mt-1 mr-3"
                                            />
                                            <MdSchedule className="text-gray-600 mr-2 mt-0.5 flex-shrink-0" size={20} />
                                            <div>
                                                <p className="font-medium text-gray-800 m-0">Pay Later</p>
                                                <p className="text-sm text-gray-600 m-0">Our team will contact you to arrange payment</p>
                                                <p className="text-xs text-gray-500 m-0 mt-1">No payment is taken now. We'll contact you via WhatsApp/email.</p>
                                            </div>
                                        </label>
                                    </div>
                                </div>

                                {/* Action Buttons - Desktop */}
                                <div className="pt-1 mt-1 hidden lg:block">
                                    <FlexboxGrid justify="space-between" align="bottom">
                                        <div className="flex items-end">
                                            <Button
                                                appearance="ghost"
                                                onClick={() => {
                                                    scrollToTop();
                                                    navigate('/book');
                                                }}
                                                size="lg"
                                                className="hover:bg-gray-100"
                                                style={{ minWidth: '120px' }}
                                            >
                                                ← Back
                                            </Button>
                                        </div>

                                        <div className="flex flex-col items-end">
                                            <p className="text-sm text-gray-600 mb-2 text-right">
                                                By placing this order, you agree to the{' '}
                                                <a className="text-blue-600 hover:text-blue-800 underline font-medium" 
                                                   href="https://www.newtonshowcamp.com/terms-and-conditions" 
                                                   target="_blank" 
                                                   rel="noreferrer">
                                                    Terms and Conditions
                                                </a>
                                            </p>
                                            <Button
                                                appearance="primary"
                                                onClick={items.length === 0 ? null : () => proceedToPayment()}
                                                size="lg"
                                                disabled={items.length === 0 || isSubmitting}
                                                loading={isSubmitting}
                                                style={{ minWidth: '160px' }}
                                            >
                                                {isSubmitting ? (paymentMethod === 'pay_later' ? 'Creating order...' : 'Preparing payment...') : (paymentMethod === 'pay_later' ? 'Confirm Booking' : 'Proceed to Payment')}
                                            </Button>
                                        </div>
                                    </FlexboxGrid>
                                </div>

                                {/* Action Buttons - Mobile (sticky footer) */}
                                <div className="block lg:hidden">
                                    <p className="text-sm text-gray-600 mb-4 text-center">
                                        By placing this order, you agree to the{' '}
                                        <a className="text-blue-600 hover:text-blue-800 underline font-medium"
                                           href="https://www.newtonshowcamp.com/terms-and-conditions"
                                           target="_blank"
                                           rel="noreferrer">
                                            Terms and Conditions
                                        </a>
                                    </p>

                                    {/* Spacer so content isn't hidden behind the fixed footer */}
                                    <div className="h-16" aria-hidden="true"></div>

                                    <div
                                        className="fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-gray-200 px-4 py-3 flex items-center gap-3"
                                        style={{ boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.08)', paddingBottom: 'calc(12px + env(safe-area-inset-bottom))' }}
                                    >
                                        <Button
                                            appearance="ghost"
                                            onClick={() => {
                                                scrollToTop();
                                                navigate('/book');
                                            }}
                                            size="lg"
                                        >
                                            Back
                                        </Button>

                                        <Button
                                            appearance="primary"
                                            onClick={items.length === 0 ? null : () => proceedToPayment()}
                                            size="lg"
                                            className="flex-1"
                                            disabled={items.length === 0 || isSubmitting}
                                            loading={isSubmitting}
                                        >
                                            {isSubmitting ? (paymentMethod === 'pay_later' ? 'Creating order...' : 'Preparing payment...') : (paymentMethod === 'pay_later' ? 'Confirm Booking' : 'Proceed to Payment')}
                                        </Button>
                                    </div>
                                </div>

                                {/* <div className="flex justify-start my-7">
                                    <p className="text-xs">*Our friendly staff will review your order and send you the invoice with the final amount</p>
                                </div> */}

                            </>
                        }
                        {step === 3 && location.pathname === '/payment-complete' &&
                            <div className="flex flex-col items-center justify-center p-8">
                                <Loader size="lg" content="Finalizing your booking..." />
                            </div>
                        }
                        {step === 3 && location.pathname !== '/payment-complete' &&
                            <>
                                {/* Payment Step */}
                                <div className="mb-6">
                                    <h1 className="page-title mb-1">Complete Your Payment</h1>
                                    <p className="text-base text-gray-600 mb-4">
                                        Secure payment for your camp order
                                    </p>
                                </div>

                                {paymentLoading ? (
                                    <div className="flex justify-center items-center p-8">
                                        <Loader size="lg" content="Loading payment..." />
                                    </div>
                                ) : paymentClientSecret ? (
                                    <div className="max-w-md mx-auto">
                                        {/* Payment Summary */}
                                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-6">
                                            <div className="text-center">
                                                <h4 className="section-heading mb-2">Payment Summary</h4>
                                                <div className="text-3xl font-bold text-gray-800 mb-1">
                                                    ${formatPrice(paymentAmount / 100)}<span className="currency-suffix">SGD</span>
                                                </div>
                                                <div className="text-sm text-gray-600">
                                                    {items.length} camp booking{items.length !== 1 ? 's' : ''}
                                                </div>
                                            </div>
                                        </div>

                                        <StripePayment
                                            clientSecret={paymentClientSecret}
                                            amount={paymentAmount}
                                            onSuccess={handlePaymentSuccess}
                                            onError={handlePaymentError}
                                        />
                                        
                                        {/* Back Button */}
                                        <div className="text-center mt-6">
                                            <Button
                                                appearance="ghost"
                                                onClick={() => navigate('/review')}
                                                size="lg"
                                            >
                                                ← Back to Review
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center p-8">
                                        <p className="text-red-600">Failed to load payment form. Please try again.</p>
                                        <Button
                                            appearance="primary"
                                            onClick={() => navigate('/review')}
                                            className="mt-4"
                                        >
                                            Back to Review
                                        </Button>
                                    </div>
                                )}
                            </>
                        }
                        {step === 4 &&
                            <div className="max-w-4xl mx-auto">
                                {/* Success Header */}
                                <div className="text-center mb-8">
                                    <div className="flex justify-center mb-4">
                                        <MdCheckCircle className="text-green-500" size={80} />
                                    </div>
                                    <h1 className="text-3xl font-bold text-gray-900 mb-2">
                                        {order?.paymentStatus === 'completed' ? 'Order Confirmed!' : 'Booking Received!'}
                                    </h1>
                                    <p className="text-lg text-gray-600">
                                        {order?.paymentStatus === 'completed'
                                            ? 'Your payment was successful.'
                                            : "We've reserved your spot. Our team will contact you to arrange payment."}
                                    </p>
                                </div>

                                {/* Payment/Order Summary — sized to its content, not the page */}
                                <div
                                    className={`${order?.paymentStatus === 'completed' ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'} border rounded-xl p-6 mb-8 mx-auto w-full`}
                                    style={{ maxWidth: 480 }}
                                >
                                    <div className="text-center">
                                        <h3 className="text-xl font-semibold text-gray-900 mb-3">
                                            {order?.paymentStatus === 'completed' ? 'Payment Confirmed' : 'Order Summary'}
                                        </h3>
                                        <div className="bg-white rounded-lg p-4 inline-block shadow-sm text-left">
                                            <p className="text-lg font-semibold text-gray-700 mb-2 text-center">Booking Number</p>
                                            <div className="flex items-center justify-center gap-2 mb-4">
                                                <span className="text-2xl font-bold text-gray-900">#{order?.orderNumber}</span>
                                                <button
                                                    type="button"
                                                    aria-label={orderNumberCopied ? 'Copied' : 'Copy booking number'}
                                                    title={orderNumberCopied ? 'Copied!' : 'Copy booking number'}
                                                    className="flex items-center text-gray-400 hover:text-blue-600 bg-transparent border-0 cursor-pointer p-1"
                                                    onClick={() => {
                                                        if (navigator.clipboard && order?.orderNumber) {
                                                            navigator.clipboard.writeText(String(order.orderNumber));
                                                            setOrderNumberCopied(true);
                                                            setTimeout(() => setOrderNumberCopied(false), 1500);
                                                        }
                                                    }}
                                                >
                                                    {orderNumberCopied
                                                        ? <MdCheck size={18} className="text-green-600" />
                                                        : <MdContentCopy size={18} />}
                                                </button>
                                            </div>
                                            {(() => {
                                                if (!order) return null;
                                                const seasonDiscountedSubtotal = order.originalAmount ||
                                                    (order.items || []).reduce((total, item) => {
                                                        const price = typeof item.price === 'string'
                                                            ? item.price.replace(/[$,]/g, '')
                                                            : item.price || 0;
                                                        return total + Number(price);
                                                    }, 0);
                                                const fullSubtotal = (order.items || []).reduce((total, item) => {
                                                    const sp = Number(item?.week?.season?.price);
                                                    if (!isNaN(sp) && sp > 0) return total + sp;
                                                    const price = typeof item.price === 'string'
                                                        ? Number(item.price.replace(/[$,]/g, ''))
                                                        : Number(item.price) || 0;
                                                    return total + price;
                                                }, 0);
                                                const seasonSaving = Math.max(0, fullSubtotal - seasonDiscountedSubtotal);
                                                const promoSaving = order.discountAmount || 0;
                                                const total = order.paidAmount != null
                                                    ? order.paidAmount
                                                    : (seasonDiscountedSubtotal - promoSaving);
                                                const totalColor = order.paymentStatus === 'completed' ? 'text-green-700' : 'text-yellow-700';

                                                return (
                                                    <div className="border-t pt-4 space-y-1.5 min-w-[16rem]">
                                                        <div className="flex justify-between text-sm">
                                                            <span className="text-gray-700">Subtotal</span>
                                                            <span className="text-gray-800">${formatPrice(fullSubtotal)}</span>
                                                        </div>
                                                        {seasonSaving > 0 && (
                                                            <div className="flex justify-between text-sm gap-6">
                                                                <span className="text-green-700">Standard discount</span>
                                                                <span className="text-green-700">−${formatPrice(seasonSaving)}</span>
                                                            </div>
                                                        )}
                                                        {promoSaving > 0 && (
                                                            <div className="flex justify-between text-sm gap-6">
                                                                <span className="text-green-700">
                                                                    Promo code{order.promoCodeString ? ` (${order.promoCodeString})` : ''}
                                                                </span>
                                                                <span className="text-green-700">−${formatPrice(promoSaving)}</span>
                                                            </div>
                                                        )}
                                                        <div className="border-t pt-2 mt-2 flex justify-between items-end">
                                                            <span className="text-gray-800 font-semibold">Total</span>
                                                            <div className="text-right">
                                                                <span className={`text-2xl font-bold ${totalColor}`}>
                                                                    ${formatPrice(total)}
                                                                </span>
                                                                <span className="currency-suffix">SGD</span>
                                                            </div>
                                                        </div>
                                                        {order.paymentStatus !== 'completed' && (
                                                            <div className="text-center mt-3">
                                                                <span className="inline-block bg-yellow-100 text-yellow-800 text-xs font-semibold rounded-full px-3 py-1">
                                                                    Payment to be arranged
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                </div>

                                {/* Order Details */}
                                {order && order.items && (
                                    <div className="bg-white border border-gray-200 rounded-xl p-6 mb-8">
                                        <h3 className="text-xl font-semibold text-gray-900 mb-6">Booking Details</h3>
                                        <div className="space-y-4">
                                            {order.items.sort((a, b) => new Date(a.week.startDate) - new Date(b.week.startDate)).map((item, index) => (
                                                <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                                                    <div className="flex justify-between items-start">
                                                        <div className="flex-1">
                                                            <h4 className="font-semibold text-lg text-gray-900 mb-2">
                                                                {item.kid.firstName} {item.kid.lastName}
                                                            </h4>
                                                            <p className="text-sm text-gray-600 mb-3">{item.week.theme}</p>
                                                            
                                                            <div className="space-y-2 text-sm">
                                                                <div className="flex items-center">
                                                                    <MdLocationOn className="text-gray-400 mr-2" size={16} />
                                                                    <span className="text-gray-700">
                                                                        {item.week.location.name}
                                                                        {item.week.location.address && (
                                                                            <span className="text-gray-500"> • {item.week.location.address}</span>
                                                                        )}
                                                                    </span>
                                                                </div>
                                                                
                                                                <div className="flex items-center">
                                                                    <MdDateRange className="text-gray-400 mr-2" size={16} />
                                                                    <span className="text-gray-700">
                                                                        {formatDateRange(item.week.startDate, item.week.endDate)}
                                                                    </span>
                                                                </div>
                                                                
                                                                <div className="flex items-center">
                                                                    <MdAccessTime className="text-gray-400 mr-2" size={16} />
                                                                    <span className="text-gray-700">{item.dayOption}</span>
                                                                </div>
                                                                
                                                                <div className="text-gray-700">
                                                                    <span className="text-gray-500">Transport:</span> {item.busOption || 'None'}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        
                                                        {item.price && (
                                                            <div className="text-right ml-4">
                                                                <div className="text-lg font-semibold text-gray-900">${formatPrice(item.price)}</div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Special Requests */}
                                {order && order.comments && (
                                    <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 mb-8">
                                        <div className="flex items-start">
                                            <MdMessage className="text-yellow-600 mr-3 mt-0.5 flex-shrink-0" size={20} />
                                            <div>
                                                <h3 className="text-lg font-semibold text-gray-900 mb-3">Special Requests</h3>
                                                <p className="text-gray-700 leading-relaxed">{order.comments}</p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Next Steps */}
                                <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">What happens next?</h3>
                                    <ol className="space-y-3 list-none p-0 m-0">
                                        {(() => {
                                            const orderItems = order?.items || [];
                                            const hasBus = orderItems.some(item => item.busOption && item.busOption !== 'None');
                                            const hasPaidBus = orderItems.some(item => item.busOption && item.busOption !== 'None' && !item.busOption.toLowerCase().includes('free'));
                                            const steps = [
                                                { text: 'You will receive a confirmation email shortly with all booking details.' }
                                            ];
                                            if (order?.paymentStatus !== 'completed') {
                                                steps.push({ text: 'Our team will contact you on WhatsApp to arrange payment.' });
                                            }
                                            if (hasBus) {
                                                steps.push({
                                                    text: 'Our customer support team will contact you to arrange bus transportation details',
                                                    muted: hasPaidBus ? ' (additional charges apply).' : '.'
                                                });
                                            }
                                            return steps.map((s, i) => (
                                                <li key={i} className="flex items-start">
                                                    <span
                                                        className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-semibold flex items-center justify-center mr-3 mt-0.5"
                                                        aria-hidden="true"
                                                    >
                                                        {i + 1}
                                                    </span>
                                                    <p className="text-gray-700 m-0">
                                                        {s.text}
                                                        {s.muted && <span className="text-gray-500">{s.muted}</span>}
                                                    </p>
                                                </li>
                                            ));
                                        })()}
                                    </ol>
                                </div>

                                {/* Contact Information */}
                                <div className="bg-gray-50 rounded-xl p-6 text-center">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Need help or have questions?</h3>
                                    <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                                        <a 
                                            href={`https://wa.me/6589501968?text=Hi%2C%20I%20need%20help%20with%20my%20camp%20booking.%20My%20order%20number%20is%20%23${order?.orderNumber || ''}.`}
                                            target="_blank" 
                                            rel="noreferrer"
                                            className="flex items-center text-blue-600 hover:text-blue-800 font-medium transition-colors"
                                        >
                                            <MdPhone className="mr-2" size={18} />
                                            WhatsApp: +65 8950 1968
                                        </a>
                                        <span className="hidden sm:block text-gray-400">•</span>
                                        <a 
                                            href="mailto:info@newtonshow.com"
                                            className="flex items-center text-blue-600 hover:text-blue-800 font-medium transition-colors"
                                        >
                                            <MdEmail className="mr-2" size={18} />
                                            info@newtonshow.com
                                        </a>
                                    </div>
                                </div>

                                {/* Navigation */}
                                <div className="text-center mt-8">
                                    <Button
                                        appearance="primary"
                                        size="lg"
                                        onClick={() => navigate('/history')}
                                        className="mr-4 hidden sm:inline-block"
                                    >
                                        View My Orders
                                    </Button>
                                    <Button
                                        appearance="ghost"
                                        size="lg"
                                        onClick={() => navigate('/book')}
                                    >
                                        Book More Camps
                                    </Button>
                                </div>
                            </div>
                        }
                    </>

                }

            </div>

            {/* Add-a-child modal, reachable from the empty-state banner and week rows */}
            {createKid && <KidInfo createKid={createKid} setCreateKid={setCreateKid} createOrUpdateKid={createOrUpdateKid} />}
        </CampLayout>
    )
}

export default Book;
